import { MenuProps, SxProps, Theme } from '@mui/material'

/**
 * Shared design tokens for the loyalty-program redesign
 * (matches `/Users/korawit/Downloads/fitup-sidebar/*-loyalty.html` mockups).
 *
 * Live MUI tokens were extracted from the mockup root variables; everything
 * here is meant to keep the 5 loyalty pages (member list, member detail,
 * POS/checkin, club settings, dashboard) visually consistent without
 * touching shared components that are used elsewhere in the app.
 */
export const RD = {
  // Brand
  primary: '#DE2826',
  primaryHover: 'rgb(155,28,26)',
  primaryPressed: 'rgb(154,5,13)',
  primaryBg: 'rgba(222,40,38,0.08)',
  primaryListBg: 'rgb(252,233,233)',
  brandDark: 'rgb(185,8,16)',

  // Surfaces
  bg: '#F5F5F5',
  surface: '#FFFFFF',
  surfaceAlt: '#FAFAFA',

  // Text
  fg: 'rgba(0,0,0,0.87)',
  fgStrong: '#000',
  fgMuted: 'rgb(121,118,118)',
  fgDisabled: 'rgba(0,0,0,0.54)',

  // Lines
  border: '#E2E2E2',
  borderInput: 'rgba(0,0,0,0.23)',
  divider: 'rgb(224,224,224)',

  // Warning (MUI Alert)
  warnIcon: 'rgb(237,108,2)',
  dangerFg: '#C72F2F',
} as const

/**
 * Status pill palette — flat fills with confident saturation.
 * Keys map to backend status codes (uppercased on read).
 */
export const PILL = {
  ACTIVE: { bg: '#F5FEE9', fg: '#33B15B' },
  EXPIRED: { bg: '#FCE9E9', fg: '#C72F2F' },
  INACTIVE: { bg: '#F0F0F0', fg: '#6B6B6B' },
  PENDING: { bg: '#FFF7E0', fg: '#8A6500' },
  BANNED: { bg: '#FCE9E9', fg: '#C72F2F' },
  DROPIN: { bg: '#E8F0FE', fg: '#1A4D8C' },
  MEMBER: { bg: '#EBF7EE', fg: '#1F6B3A' },
  FREEPASS: { bg: '#FFF4E5', fg: '#8A5300' },
  /** Used by the Member Detail header's "Member Type" pill — tonal red. */
  TYPE: { bg: '#FCE9E9', fg: '#C74C3F' },
  UNKNOWN: { bg: '#F0F0F0', fg: '#6B6B6B' },
} as const

export type PillKind = keyof typeof PILL

export const pillSx = (kind: PillKind): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  px: 1.25,
  py: 0.25,
  borderRadius: '999px',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  bgcolor: PILL[kind].bg,
  color: PILL[kind].fg,
})

/** H1 — 18px uppercase-ish, brand-dark, weight 800. */
export const titleSx: SxProps<Theme> = {
  fontSize: 18,
  fontWeight: 800,
  lineHeight: 1.4,
  letterSpacing: '0.15px',
  color: RD.brandDark,
  m: 0,
}

/** Primary action button — 12px UPPERCASE weight 700, radius 6px. */
export const primaryBtnSx: SxProps<Theme> = {
  minHeight: 32,
  px: 1.5,
  py: 0.75,
  borderRadius: '6px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
  bgcolor: RD.primary,
  color: '#fff',
  '&:hover': { bgcolor: RD.primaryHover, boxShadow: '0 2px 4px rgba(0,0,0,0.18)' },
  '&:active': { bgcolor: RD.primaryPressed },
}

/** Member ID — monospace, tabular. */
export const memberIdSx: SxProps<Theme> = {
  fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, Menlo, monospace',
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '0.2px',
  color: RD.fg,
}

/** Numeric cell — tabular nums for clean column alignment. */
export const numCellSx: SxProps<Theme> = {
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}

/** White card surface used across the loyalty redesign (member header,
 *  loyalty band, history tables). 16px radius, light shadow. */
export const cardSurfaceSx: SxProps<Theme> = {
  bgcolor: RD.surface,
  borderRadius: '16px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
}

/** Pill toggle container — full-width card holding the segmented pills. */
export const tabsPillContainerSx: SxProps<Theme> = {
  bgcolor: RD.surface,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  p: 0.5,
  minHeight: 'auto',
  display: 'flex',
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto',
  '& .MuiTabs-flexContainer': { gap: 0.5 },
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTabs-scrollButtons': { color: RD.fgMuted },
}

/** Individual pill tab — selected = brand-red filled, unselected = subtle. */
export const tabPillSx: SxProps<Theme> = {
  minHeight: 40,
  height: 40,
  px: 2,
  py: 1,
  borderRadius: '8px',
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  color: 'rgba(0,0,0,0.6)',
  transition: 'background-color 160ms ease, color 160ms ease',
  '&:hover': { color: RD.fg, bgcolor: 'rgba(0,0,0,0.04)' },
  '&.Mui-selected': {
    color: '#fff',
    bgcolor: RD.primary,
    '&:hover': { bgcolor: RD.brandDark },
  },
}

/**
 * Outlined Select / TextField wrapper styles for the loyalty redesign
 * filter row. Pins border, label, and arrow caret to the mockup palette
 * so the app-wide MUI theme doesn't bleed through (red focus state stays,
 * everything else is neutral grey).
 */
export const filterSelectSx: SxProps<Theme> = {
  minWidth: 160,
  '& .MuiOutlinedInput-root': {
    bgcolor: RD.surface,
    borderRadius: '4px',
    fontSize: 14,
    color: RD.fg,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: RD.borderInput },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: RD.fgStrong },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: RD.primary,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: RD.fgMuted,
    fontSize: 12,
    '&.Mui-focused': { color: RD.primary },
  },
  '& .MuiSelect-icon': { color: RD.fgDisabled },
}

/**
 * Popover styling shared by every loyalty filter Select. Clean white paper,
 * 10px radius, soft elevation matching the table card, no greasy MUI default
 * hover — items just tint primary-red on hover and active.
 */
export const selectMenuProps: Partial<MenuProps> = {
  slotProps: {
    paper: {
      elevation: 0,
      sx: {
        mt: 0.5,
        borderRadius: '10px',
        bgcolor: RD.surface,
        border: `1px solid ${RD.border}`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        '& .MuiList-root': { py: 0.5 },
        '& .MuiMenuItem-root': {
          mx: 0.5,
          my: 0.25,
          px: 1.5,
          py: 1,
          borderRadius: '6px',
          fontSize: 14,
          color: RD.fg,
          '&:hover': { bgcolor: RD.primaryBg, color: RD.brandDark },
          '&.Mui-selected': {
            bgcolor: RD.primaryListBg,
            color: RD.brandDark,
            fontWeight: 600,
            '&:hover': { bgcolor: RD.primaryListBg },
          },
        },
      },
    },
  },
}

/**
 * Search field — 300px pill at 36px tall. White bg (the app's body bg is
 * already #FAFAFA so the mockup's #FAFAFA pill would be invisible — using
 * surface white instead so the search sits as a clear card on the page).
 */
export const searchFieldSx: SxProps<Theme> = {
  width: 300,
  '& .MuiOutlinedInput-root': {
    height: 36,
    bgcolor: RD.surface,
    borderRadius: '10px',
    fontSize: 13,
    color: RD.fg,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: RD.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: RD.fgStrong },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: RD.primary,
      borderWidth: 1,
    },
    '& input': {
      color: RD.fg,
      '&::placeholder': { color: RD.fgMuted, opacity: 1 },
    },
  },
}

/**
 * ── Product feature redesign ──────────────────────────────────────────────
 *
 * Tokens + sx primitives shared by Products & Stock, Product Sale (POS), and
 * Sales History & Void. They are a 1:1 port of the CSS custom properties and
 * class rules in `/Users/korawit/Downloads/fitup-product-system/*.html`
 * (`:root` light values, `:root[data-theme="dark"]` dark values), so the three
 * pages stay one coherent design instead of page-local overrides.
 *
 * Everything is expressed as `(theme) => styles` so the app's MUI color mode
 * keeps working — pass primitives straight to `sx`, or spread them inside
 * another theme callback.
 */
export interface ProductPalette {
  primary: string
  primaryDeep: string
  primaryInk: string
  primaryContainer: string
  secondaryContainer: string
  surface: string
  surfaceLow: string
  surfaceContainer: string
  surfaceHigh: string
  onSurface: string
  onSurfaceVariant: string
  outline: string
  outlineVariant: string
  iconInactive: string
  cash: string
  cashBg: string
  credit: string
  creditBg: string
  success: string
  successBg: string
  successFg: string
  successPillBg: string
  error: string
  errorBg: string
  warn: string
  warnBg: string
  infoPillBg: string
  infoPillFg: string
  hover: string
  shadow: string
}

export const PRODUCT_PALETTE: Record<'light' | 'dark', ProductPalette> = {
  light: {
    primary: '#DE2826',
    primaryDeep: '#B90810',
    primaryInk: '#B90810',
    primaryContainer: '#F8D3D3',
    secondaryContainer: '#FCE9E9',
    surface: '#FFFFFF',
    surfaceLow: '#FAFAFA',
    surfaceContainer: '#F1F1F1',
    surfaceHigh: '#E3E2E2',
    onSurface: '#000000',
    onSurfaceVariant: 'rgba(0,0,0,0.6)',
    outline: 'rgb(188,188,188)',
    outlineVariant: 'rgb(224,224,224)',
    iconInactive: '#C9C5C6',
    cash: '#1F7A2E',
    cashBg: '#E5F4E8',
    credit: '#8250DF',
    creditBg: '#F1EAFD',
    success: '#1F7A2E',
    successBg: '#E5F4E8',
    successFg: '#33B15B',
    successPillBg: '#F5FEE9',
    error: '#B90810',
    errorBg: '#FDE5E5',
    warn: '#B97400',
    warnBg: '#FFF1D6',
    infoPillBg: '#E8F3FF',
    infoPillFg: '#0D5EB6',
    hover: 'rgba(0,0,0,0.04)',
    shadow: '0 1px 4px rgba(0,0,0,0.10)',
  },
  dark: {
    primary: '#E5423F',
    primaryDeep: '#C4302D',
    primaryInk: '#FF8A87',
    primaryContainer: '#4A1414',
    secondaryContainer: '#3B1615',
    surface: '#1C1C1E',
    surfaceLow: '#242426',
    surfaceContainer: '#2C2C2E',
    surfaceHigh: '#3A3A3C',
    onSurface: '#F2F2F3',
    onSurfaceVariant: 'rgba(255,255,255,0.62)',
    outline: '#5B5B60',
    outlineVariant: '#3A3A3E',
    iconInactive: '#6E6A6C',
    cash: '#5BD47F',
    cashBg: 'rgba(51,177,91,0.16)',
    credit: '#B79AF2',
    creditBg: 'rgba(130,80,223,0.22)',
    success: '#5BD47F',
    successBg: 'rgba(51,177,91,0.16)',
    successFg: '#5BD47F',
    successPillBg: 'rgba(51,177,91,0.14)',
    error: '#FF6B67',
    errorBg: 'rgba(222,40,38,0.20)',
    warn: '#E5A54B',
    warnBg: 'rgba(185,116,0,0.20)',
    infoPillBg: 'rgba(13,94,182,0.25)',
    infoPillFg: '#7CB5F0',
    hover: 'rgba(255,255,255,0.07)',
    shadow: '0 1px 4px rgba(0,0,0,0.5)',
  },
}

/** Resolve the product palette for the active MUI color mode. */
export const pt = (theme: Theme): ProductPalette =>
  theme.palette.mode === 'dark' ? PRODUCT_PALETTE.dark : PRODUCT_PALETTE.light

/** Mockup `.pc-img` / `.p-thumb` fallback colors, assigned per product. */
export const PRODUCT_THUMB_COLORS = [
  '#0D5EB6',
  '#1F7A2E',
  '#8250DF',
  '#B90810',
  '#91288D',
  '#B97400',
  '#2E3845',
] as const

export const productThumbColor = (seed: string) => {
  let hash = 0
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0
  }
  return PRODUCT_THUMB_COLORS[hash % PRODUCT_THUMB_COLORS.length]
}

export const productMonoFont = 'ui-monospace, Menlo, monospace'

/** `.main` — 32px padding, 20px vertical rhythm between sections. */
export const productPageSx = (theme: Theme) => ({
  p: { xs: 2, md: 4 },
  display: 'flex',
  flexDirection: 'column',
  gap: 2.5,
  fontSize: 14,
  letterSpacing: '0.15px',
  color: pt(theme).onSurface,
})

/** `.paper` */
export const productPaperSx = (theme: Theme) => ({
  bgcolor: pt(theme).surface,
  borderRadius: '16px',
  boxShadow: pt(theme).shadow,
  backgroundImage: 'none',
})

/** `.topbar` */
export const productTopbarSx = () => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
})

/** `.crumbs` */
export const productCrumbSx = (theme: Theme) => ({
  fontSize: '0.875rem',
  color: pt(theme).onSurfaceVariant,
  mb: 0.75,
  '& strong': { color: pt(theme).onSurface, fontWeight: 700 },
})

/** `.topbar h1` */
export const productTitleSx = (theme: Theme) => ({
  m: 0,
  fontSize: 22,
  lineHeight: 1.2,
  fontWeight: 700,
  color: pt(theme).primary,
})

/** `.topbar-actions` */
export const productTopbarActionsSx = () => ({
  display: 'flex',
  gap: 1.25,
  alignItems: 'center',
  flexWrap: 'wrap',
})

/** `.btn` base — 36px pill-ish button, radius 16. */
export const productBtnSx = (theme: Theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.75,
  px: 2.75,
  py: 1,
  minHeight: 36,
  border: 0,
  borderRadius: '16px',
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '0.3px',
  lineHeight: 1.45,
  textTransform: 'none',
  bgcolor: pt(theme).surface,
  color: pt(theme).onSurface,
  boxShadow: 'none',
})

/** `.btn-primary` */
export const productBtnPrimarySx = (theme: Theme) => ({
  ...productBtnSx(theme),
  bgcolor: pt(theme).primary,
  color: '#fff',
  boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
  '&:hover': { bgcolor: pt(theme).primaryDeep, boxShadow: '0 1px 5px rgba(0,0,0,0.2)' },
  '&.Mui-disabled': {
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
    boxShadow: 'none',
  },
})

/** `.btn-primary` with the error background (delete / void confirm). */
export const productBtnDangerSx = (theme: Theme) => ({
  ...productBtnPrimarySx(theme),
  bgcolor: pt(theme).error,
  '&:hover': { bgcolor: pt(theme).error, boxShadow: '0 1px 5px rgba(0,0,0,0.2)' },
})

/** `.btn-outline` */
export const productBtnOutlineSx = (theme: Theme) => ({
  ...productBtnSx(theme),
  border: '1px solid rgba(222,40,38,0.5)',
  color: pt(theme).primary,
  '&:hover': {
    bgcolor: 'rgba(222,40,38,0.06)',
    borderColor: pt(theme).primary,
  },
})

/** `.btn-ghost` */
export const productBtnGhostSx = (theme: Theme) => ({
  ...productBtnSx(theme),
  bgcolor: 'transparent',
  color: pt(theme).primary,
  '&:hover': { bgcolor: 'rgba(222,40,38,0.08)' },
})

/** `.btn-lg` — 52px full-width checkout-style button. */
export const productBtnLgSx = (theme: Theme) => ({
  ...productBtnPrimarySx(theme),
  minHeight: 52,
  px: 3.5,
  py: 1.5,
  fontSize: 15,
  fontWeight: 700,
  borderRadius: '14px',
  width: '100%',
})

/** `.page-tabs` — fit-content pill container. */
export const productPageTabsSx = (theme: Theme) => ({
  display: 'flex',
  gap: 0.5,
  p: 0.75,
  bgcolor: pt(theme).surface,
  borderRadius: '16px',
  boxShadow: pt(theme).shadow,
  width: 'fit-content',
  maxWidth: '100%',
})

/** `.page-tab` */
export const productPageTabSx = (theme: Theme, active: boolean) => ({
  border: 0,
  minHeight: 0,
  bgcolor: active ? pt(theme).secondaryContainer : 'transparent',
  px: 2.5,
  py: 1.125,
  borderRadius: '11px',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.45,
  textTransform: 'none',
  color: active ? pt(theme).primaryInk : pt(theme).onSurfaceVariant,
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  '&:hover': { bgcolor: active ? pt(theme).secondaryContainer : pt(theme).hover },
})

/** `.page-tab .cnt` */
export const productTabCountSx = (theme: Theme, variant: 'primary' | 'warn' = 'primary') => ({
  fontSize: 11,
  fontWeight: 700,
  bgcolor: variant === 'warn' ? pt(theme).warn : pt(theme).primary,
  color: '#fff',
  borderRadius: '9999px',
  px: 0.875,
  py: '1px',
  lineHeight: '15px',
})

/** `.filter-bar` */
export const productFilterBarSx = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 2.5,
  py: 2,
  flexWrap: 'wrap',
})

/** `.f-search` — search input, 40px, icon on the left. */
export const productSearchSx = (theme: Theme) => ({
  flex: '1 1 240px',
  maxWidth: 340,
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '10px',
    bgcolor: pt(theme).surfaceLow,
    fontSize: 13,
    color: pt(theme).onSurface,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).outlineVariant },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).outlineVariant },
    '&.Mui-focused': {
      bgcolor: pt(theme).surface,
      boxShadow: '0 0 0 3px rgba(222,40,38,0.12)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).primary, borderWidth: 1 },
    },
    '& input::placeholder': { color: pt(theme).onSurfaceVariant, opacity: 1 },
  },
  '& .MuiInputAdornment-root': { color: pt(theme).onSurfaceVariant },
})

/** `.f-chip` */
export const productChipSx = (theme: Theme, active: boolean) => ({
  border: `1px solid ${active ? pt(theme).primary : pt(theme).outlineVariant}`,
  bgcolor: active ? pt(theme).primary : pt(theme).surface,
  borderRadius: '9999px',
  px: 2,
  py: 0.875,
  minWidth: 0,
  minHeight: 0,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.45,
  textTransform: 'none',
  color: active ? '#fff' : pt(theme).onSurfaceVariant,
  '&:hover': {
    bgcolor: active ? pt(theme).primary : pt(theme).surface,
    borderColor: active ? pt(theme).primary : 'rgba(222,40,38,0.4)',
  },
})

/** `.f-sel` — 40px borderless-label filter select. */
export const productFilterSelectSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '10px',
    bgcolor: pt(theme).surface,
    fontSize: 13,
    fontWeight: 600,
    color: pt(theme).onSurface,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).outlineVariant },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).outline },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: pt(theme).primary,
      borderWidth: 1,
    },
  },
  '& .MuiSelect-icon': { color: pt(theme).onSurfaceVariant },
})

/** `table.tbl` — apply on the MUI `Table` so every th/td matches the mockup. */
export const productTableSx = (theme: Theme) => ({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13.5,
  '& th': {
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: pt(theme).onSurfaceVariant,
    px: 2,
    py: 1.5,
    border: 0,
    borderBottom: `1px solid ${pt(theme).outlineVariant}`,
    bgcolor: pt(theme).surfaceLow,
    whiteSpace: 'nowrap',
    lineHeight: 1.45,
  },
  '& td': {
    px: 2,
    py: 1.625,
    border: 0,
    borderBottom: `1px solid ${pt(theme).outlineVariant}`,
    verticalAlign: 'middle',
    fontSize: 13.5,
    lineHeight: 1.45,
    color: pt(theme).onSurface,
  },
  '& tr:last-child td': { borderBottom: 0 },
})

/** `.tbl .num` */
export const productNumCellSx = () => ({
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  textAlign: 'right',
})

/**
 * Overrides for a multi-select whose value renders as chips — pass it AFTER
 * productFieldSx (`sx={[productFieldSx, productChipsSelectSx]}`). productFieldSx
 * pins non-multiline inputs to 44px, which clips chips once they wrap; here the
 * control grows with the rows instead.
 */
export const productChipsSelectSx = () => ({
  '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': { height: 'auto', minHeight: 44 },
  '& .MuiSelect-select': {
    whiteSpace: 'normal',
    height: 'auto',
    minHeight: 'unset',
    py: 0.75,
  },
})

/** Selected-branch chip inside the "sold at" multi-select. */
export const productBranchChipSx = (theme: Theme) => ({
  height: 22,
  borderRadius: '9999px',
  bgcolor: pt(theme).hover,
  color: pt(theme).onSurface,
  fontSize: 11.5,
  fontWeight: 600,
  '& .MuiChip-deleteIcon': {
    fontSize: 14,
    color: pt(theme).onSurfaceVariant,
    '&:hover': { color: pt(theme).onSurface },
  },
})

/** `.pay-chip` */
export const productPayChipSx = (theme: Theme, type: 'cash' | 'credit') => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.625,
  height: 22,
  px: 1.25,
  borderRadius: '9999px',
  fontSize: 11.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: type === 'cash' ? pt(theme).cashBg : pt(theme).creditBg,
  color: type === 'cash' ? pt(theme).cash : pt(theme).credit,
})

export type ProductStockPillState = 'ok' | 'low' | 'out' | 'na'

/** `.stock-pill` — dot + label. */
export const productStockPillSx = (theme: Theme, state: ProductStockPillState) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.625,
  fontSize: 12,
  fontWeight: state === 'na' ? 500 : 600,
  color:
    state === 'low'
      ? pt(theme).warn
      : state === 'out'
        ? pt(theme).error
        : state === 'na'
          ? pt(theme).onSurfaceVariant
          : pt(theme).onSurface,
  '&::before':
    state === 'na'
      ? undefined
      : {
          content: '""',
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor:
            state === 'ok'
              ? pt(theme).successFg
              : state === 'low'
                ? pt(theme).warn
                : pt(theme).error,
        },
})

/** `.mini-tag.exp` */
export const productMiniTagSx = (theme: Theme) => ({
  display: 'inline-flex',
  height: 18,
  alignItems: 'center',
  px: 0.875,
  borderRadius: '5px',
  fontSize: 10.5,
  fontWeight: 700,
  ml: 0.75,
  bgcolor: pt(theme).warnBg,
  color: pt(theme).warn,
})

/** `.status-pill` — on/off (catalog) and paid/void (sales history). */
export const productStatusPillSx = (theme: Theme, state: 'on' | 'off' | 'paid' | 'void') => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  height: 24,
  px: 1.375,
  borderRadius: '9999px',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor:
    state === 'on' || state === 'paid'
      ? pt(theme).successPillBg
      : state === 'void'
        ? pt(theme).errorBg
        : pt(theme).surfaceContainer,
  color:
    state === 'on' || state === 'paid'
      ? pt(theme).successFg
      : state === 'void'
        ? pt(theme).error
        : pt(theme).onSurfaceVariant,
  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    bgcolor: 'currentColor',
  },
})

/** `.icon-btn` */
export const productIconBtnSx = (theme: Theme, danger = false) => ({
  width: 32,
  height: 32,
  borderRadius: '8px',
  color: pt(theme).onSurfaceVariant,
  '&:hover': danger
    ? { bgcolor: pt(theme).errorBg, color: pt(theme).error }
    : { bgcolor: pt(theme).hover, color: pt(theme).onSurface },
  '& svg': { width: 16, height: 16 },
})

/** `.empty-note` */
export const productEmptyNoteSx = (theme: Theme) => ({
  p: 5,
  textAlign: 'center',
  color: pt(theme).onSurfaceVariant,
  fontSize: 13,
})

/** `.kpi` */
export const productKpiSx = () => ({
  px: 2.5,
  py: 2.25,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
})

export const productKpiLabelSx = (theme: Theme) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: pt(theme).onSurfaceVariant,
})

export const productKpiValueSx = (theme: Theme, tone: 'default' | 'warn' | 'err' = 'default') => ({
  fontSize: 30,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: '36px',
  color: tone === 'warn' ? pt(theme).warn : tone === 'err' ? pt(theme).error : pt(theme).onSurface,
})

export const productKpiSubSx = (theme: Theme) => ({
  fontSize: 12,
  color: pt(theme).onSurfaceVariant,
})

/** `.sec-head` */
export const productSecHeadSx = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 2.5,
  pt: 2.25,
  pb: 0.5,
  flexWrap: 'wrap',
})

/**
 * `.fld` — 44px outlined form field with the label sitting on the top border.
 * Works for TextField and Select (pass `slotProps={{ inputLabel: { shrink: true } }}`).
 */
export const productFieldSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: 14,
    bgcolor: pt(theme).surface,
    color: pt(theme).onSurface,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).outline },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: pt(theme).outline },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: pt(theme).primary,
      borderWidth: 2,
    },
    '&:not(.MuiInputBase-multiline)': { height: 44 },
    '& input::placeholder': { color: pt(theme).onSurfaceVariant, opacity: 0.8 },
  },
  '& .MuiInputLabel-root': {
    fontSize: 14,
    fontWeight: 500,
    color: pt(theme).onSurfaceVariant,
    '&.Mui-focused': { color: pt(theme).primary },
  },
  '& .MuiSelect-icon': { color: pt(theme).onSurfaceVariant },
  '& .MuiInputAdornment-root .MuiTypography-root': {
    fontSize: 12,
    fontWeight: 600,
    color: pt(theme).onSurfaceVariant,
  },
})

/** `.form-row` — two-column field grid (single column under 720px). */
export const productFormRowSx = () => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 1.75,
  mb: 1.75,
})

export const productFormRowSingleSx = () => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 1.75,
  mb: 1.75,
})

/** `.f-hint` */
export const productFieldHintSx = (theme: Theme) => ({
  fontSize: 11.5,
  color: pt(theme).onSurfaceVariant,
  mt: -1,
  mb: 1.75,
  ml: 0.25,
  lineHeight: '15px',
})

/** `.b-sub-title` — small section heading with the 3px red bar. */
export const productSubTitleSx = (theme: Theme) => ({
  fontSize: 13,
  fontWeight: 700,
  color: pt(theme).onSurface,
  mt: 2.5,
  mb: 1.75,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  '&::before': {
    content: '""',
    width: 3,
    height: 14,
    bgcolor: pt(theme).primary,
    borderRadius: '2px',
  },
})

/** `.tile-grid` */
export const productTileGridSx = () => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 1.25,
  mb: 2,
})

/** `.tile` — selectable option tile; accent defaults to the primary red. */
export const productTileSx = (theme: Theme, selected: boolean, accent?: string) => {
  const color = accent ?? pt(theme).primary
  return {
    p: '14px 12px',
    border: `1px solid ${selected ? color : pt(theme).outlineVariant}`,
    borderRadius: '12px',
    bgcolor: selected ? alphaTint(color, 0.06) : pt(theme).surface,
    boxShadow: selected ? `0 0 0 1px ${color}` : 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.5,
    textAlign: 'left',
    transition: 'all 0.15s',
    position: 'relative',
    color: pt(theme).onSurface,
    textTransform: 'none',
    minWidth: 0,
    '&:hover': {
      bgcolor: selected ? alphaTint(color, 0.06) : pt(theme).surface,
      borderColor: selected ? color : 'rgba(222,40,38,0.4)',
    },
  }
}

export const productTileNameSx = (theme: Theme, selected: boolean, accent?: string) => ({
  fontSize: 13,
  fontWeight: 700,
  lineHeight: '17px',
  display: 'flex',
  alignItems: 'center',
  gap: 0.875,
  color: selected ? (accent ?? pt(theme).primaryInk) : pt(theme).onSurface,
})

export const productTileDescSx = (theme: Theme) => ({
  fontSize: 11,
  color: pt(theme).onSurfaceVariant,
  lineHeight: '14px',
})

/** `.tile .t-check` */
export const productTileCheckSx = (theme: Theme, selected: boolean, accent?: string) => {
  const color = accent ?? pt(theme).primary
  return {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `1.5px solid ${selected ? color : pt(theme).outlineVariant}`,
    bgcolor: selected ? color : pt(theme).surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { width: 12, height: 12, color: '#fff', opacity: selected ? 1 : 0 },
  }
}

/** `.opt-row` — bordered switch row. */
export const productOptRowSx = (theme: Theme, expanded: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 1.75,
  py: 1.5,
  border: `1px solid ${expanded ? pt(theme).primary : pt(theme).outlineVariant}`,
  borderRadius: '10px',
  cursor: 'pointer',
  userSelect: 'none',
  mb: 1.75,
  bgcolor: expanded ? 'rgba(222,40,38,0.05)' : 'transparent',
})

/** `.note-box` */
export const productNoteBoxSx = (theme: Theme, kind: 'warn' | 'info' | 'err' | 'ok') => ({
  display: 'flex',
  gap: 1,
  alignItems: 'flex-start',
  borderRadius: '10px',
  px: 1.5,
  py: 1.25,
  fontSize: 12,
  lineHeight: 1.55,
  bgcolor:
    kind === 'warn'
      ? pt(theme).warnBg
      : kind === 'info'
        ? pt(theme).infoPillBg
        : kind === 'err'
          ? pt(theme).errorBg
          : pt(theme).successBg,
  color:
    kind === 'warn'
      ? pt(theme).warn
      : kind === 'info'
        ? pt(theme).infoPillFg
        : kind === 'err'
          ? pt(theme).error
          : pt(theme).success,
  '& svg': { width: 15, height: 15, flexShrink: 0, mt: '1px' },
})

/** `.img-drop` */
export const productImgDropSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.75,
  border: `1.5px dashed ${pt(theme).outline}`,
  borderRadius: '12px',
  p: 1.75,
  mb: 1.75,
  cursor: 'pointer',
  transition: 'border-color .15s',
  '&:hover': { borderColor: pt(theme).primary },
})

/** `.modal` paper — pass through Dialog `PaperProps.sx`. */
export const productModalPaperSx = (theme: Theme) => ({
  bgcolor: pt(theme).surface,
  backgroundImage: 'none',
  borderRadius: '16px',
  boxShadow: '0 11px 15px -7px rgba(0,0,0,0.20), 0 24px 38px 3px rgba(0,0,0,0.14)',
})

/** `.modal-title` */
export const productModalTitleSx = (theme: Theme) => ({
  px: 3.25,
  pt: 2.75,
  pb: 1.75,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  borderBottom: `1px solid ${pt(theme).outlineVariant}`,
  flexShrink: 0,
})

export const productModalHeadingSx = (theme: Theme) => ({
  m: 0,
  fontSize: 17,
  fontWeight: 700,
  color: pt(theme).onSurface,
})

export const productModalSubSx = (theme: Theme) => ({
  m: 0,
  mt: 0.25,
  fontSize: 12,
  color: pt(theme).onSurfaceVariant,
  fontWeight: 400,
})

/** `.modal-body` */
export const productModalBodySx = () => ({
  px: 3.25,
  pt: 2.75,
  pb: 1,
  overflowY: 'auto',
})

/** `.modal-foot` */
export const productModalFootSx = (theme: Theme) => ({
  px: 3.25,
  pt: 1.75,
  pb: 2.5,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1.25,
  borderTop: `1px solid ${pt(theme).outlineVariant}`,
})

/** `.pg-btn` — POS pager buttons (also used for paginated tables). */
export const productPagerBtnSx = (theme: Theme, active = false) => ({
  minWidth: 34,
  height: 34,
  px: 1.25,
  border: `1px solid ${active ? pt(theme).primary : pt(theme).outlineVariant}`,
  bgcolor: active ? pt(theme).primary : pt(theme).surface,
  borderRadius: '9px',
  fontSize: 13,
  fontWeight: 600,
  color: active ? '#fff' : pt(theme).onSurfaceVariant,
  fontVariantNumeric: 'tabular-nums',
  textTransform: 'none',
  lineHeight: 1,
  '&:hover': {
    bgcolor: active ? pt(theme).primary : pt(theme).surface,
    borderColor: active ? pt(theme).primary : 'rgba(222,40,38,0.4)',
    color: active ? '#fff' : pt(theme).onSurface,
  },
  '&.Mui-disabled': { opacity: 0.4, color: pt(theme).onSurfaceVariant },
})

/** `.pager` */
export const productPagerSx = () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.75,
  flexWrap: 'wrap',
})

/** Simple alpha tint used by tiles/lanes (`rgba(<accent>, x)` in the mockup). */
const alphaTint = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const numeric = parseInt(
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value,
    16
  )
  const r = (numeric >> 16) & 255
  const g = (numeric >> 8) & 255
  const b = numeric & 255
  return `rgba(${r},${g},${b},${alpha})`
}

export const productAlphaTint = alphaTint

/** `.b-card` (POS builder cards). */
export const productBCardSx = (theme: Theme) => ({
  ...productPaperSx(theme),
  p: 3,
})

/** `.b-num` — numbered circle in POS card headers. */
export const productBNumSx = (theme: Theme) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  bgcolor: pt(theme).secondaryContainer,
  color: pt(theme).primaryInk,
  fontWeight: 700,
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
})

/** `.qty-ctl button` — 24px stepper buttons in the cart. */
export const productQtyBtnSx = (theme: Theme) => ({
  width: 24,
  height: 24,
  minWidth: 24,
  p: 0,
  border: `1px solid ${pt(theme).outlineVariant}`,
  bgcolor: pt(theme).surface,
  borderRadius: '7px',
  fontSize: 14,
  fontWeight: 700,
  color: pt(theme).onSurface,
  lineHeight: 1,
  '&:hover': {
    borderColor: pt(theme).primary,
    color: pt(theme).primary,
    bgcolor: pt(theme).surface,
  },
})

/** `.sum-line` */
export const productSumLineSx = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 0.875,
  fontSize: 13.5,
  '& .lab': { color: pt(theme).onSurfaceVariant },
  '& .val': { fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
})

/** `.sum-total` */
export const productSumTotalSx = (theme: Theme) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  pt: 1.625,
  mt: 0.75,
  borderTop: `1.5px solid ${pt(theme).onSurface}`,
  '& .lab': { fontSize: 14, fontWeight: 700 },
  '& .val': {
    fontSize: 24,
    fontWeight: 700,
    color: pt(theme).primary,
    fontVariantNumeric: 'tabular-nums',
  },
})

/** `.pay-card` — payment-method radio card (cash lane). */
export const productPayCardSx = (theme: Theme, selected: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 1,
  px: 1.5,
  py: 1.375,
  border: `1px solid ${selected ? pt(theme).primary : pt(theme).outlineVariant}`,
  borderRadius: '12px',
  bgcolor: selected ? 'rgba(222,40,38,0.04)' : pt(theme).surface,
  boxShadow: selected ? `0 0 0 1px ${pt(theme).primary}` : 'none',
  transition: 'all .15s',
  textTransform: 'none',
  minWidth: 0,
  color: pt(theme).onSurface,
  '&:hover': {
    borderColor: selected ? pt(theme).primary : 'rgba(222,40,38,0.4)',
    bgcolor: selected ? 'rgba(222,40,38,0.04)' : pt(theme).surface,
  },
})

/** `.pay-card .pay-radio` */
export const productPayRadioSx = (theme: Theme, selected: boolean) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: `2px solid ${selected ? pt(theme).primary : pt(theme).outline}`,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': selected
    ? {
        content: '""',
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: pt(theme).primary,
      }
    : undefined,
})

/** `.change-strip` */
export const productChangeStripSx = (theme: Theme, tone: 'idle' | 'ok' | 'short') => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  mt: 1.5,
  px: 1.75,
  py: 1.5,
  borderRadius: '10px',
  bgcolor: pt(theme).surfaceLow,
  border: `1px solid ${pt(theme).outlineVariant}`,
  '& .lab': { fontSize: 12, fontWeight: 700, color: pt(theme).onSurfaceVariant },
  '& .val': {
    fontSize: 22,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color:
      tone === 'ok' ? pt(theme).success : tone === 'short' ? pt(theme).error : pt(theme).onSurface,
  },
})

/** `.receipt` + `.rv-row` (POS success modal). */
export const productReceiptSx = (theme: Theme) => ({
  mx: 3.25,
  mt: 2.25,
  border: `1px solid ${pt(theme).outlineVariant}`,
  borderRadius: '12px',
  overflow: 'hidden',
  '& .rv-row': {
    display: 'grid',
    gridTemplateColumns: '130px 1fr',
    px: 2,
    py: 1.25,
    borderBottom: `1px solid ${pt(theme).outlineVariant}`,
    fontSize: 13,
  },
  '& .rv-row:last-child': { borderBottom: 0 },
  '& .rv-lab': { color: pt(theme).onSurfaceVariant },
  '& .rv-val': { fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  '& .rv-row.hl': { bgcolor: pt(theme).surfaceLow },
  '& .rv-row.hl .rv-val': { fontSize: 17, fontWeight: 700, color: pt(theme).primary },
})

/** `.void-hint` */
export const productVoidHintSx = (theme: Theme) => ({
  fontSize: 11.5,
  color: pt(theme).onSurfaceVariant,
  textAlign: 'center',
  mt: 0.5,
  '& a': { color: pt(theme).primary, fontWeight: 600, textDecoration: 'none' },
})

/** `.bill-sum` (void modal receipt summary). */
export const productBillSumSx = (theme: Theme) => ({
  border: `1px solid ${pt(theme).outlineVariant}`,
  borderRadius: '10px',
  mb: 2,
  overflow: 'hidden',
  '& .r': {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    px: 1.75,
    py: 1.125,
    borderBottom: `1px solid ${pt(theme).outlineVariant}`,
    fontSize: 12.5,
  },
  '& .r:last-child': { borderBottom: 0 },
  '& .r .l': { color: pt(theme).onSurfaceVariant },
  '& .r .v': { fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
})

/** `.auto-item` (void modal "done automatically" list). */
export const productAutoItemSx = (theme: Theme) => ({
  display: 'flex',
  gap: 1.25,
  alignItems: 'flex-start',
  px: 1.5,
  py: 1.25,
  border: `1px solid ${pt(theme).outlineVariant}`,
  borderRadius: '10px',
  fontSize: 12.5,
  lineHeight: '17px',
  '& > svg': { width: 16, height: 16, flexShrink: 0, color: pt(theme).success, mt: '1px' },
})

/** `.btn-void` (sales-history row action). */
export const productVoidBtnSx = (theme: Theme) => ({
  border: '1px solid rgba(222,40,38,0.5)',
  bgcolor: pt(theme).surface,
  color: pt(theme).primary,
  borderRadius: '10px',
  px: 2,
  py: 0.75,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 600,
  textTransform: 'none',
  lineHeight: 1.45,
  '&:hover': { bgcolor: 'rgba(222,40,38,0.05)', borderColor: 'rgba(222,40,38,0.5)' },
  '&.Mui-disabled': {
    borderColor: pt(theme).outlineVariant,
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    bgcolor: pt(theme).surfaceLow,
  },
})

/** `.switch` — 40×22 mockup switch, applied to MUI `Switch`. */
export const productSwitchSx = (theme: Theme) => ({
  width: 40,
  height: 22,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: '3px',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': { bgcolor: pt(theme).primary, opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
    bgcolor: '#fff',
  },
  '& .MuiSwitch-track': { borderRadius: 11, bgcolor: pt(theme).outline, opacity: 1 },
})
